// src/components/HotelQuickOptions.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// Quick Options Row: Cheapest, Best Rated, Best Location
// Matches mockup design
// ═══════════════════════════════════════════════════════════════════════════════

import React from 'react';
import './HotelQuickOptions.css';

const HotelQuickOptions = ({ quickOptions, hotels = [], onSelectHotel, hotelParams }) => {
  // Safety check - if no hotels, don't render
  if (!hotels || hotels.length === 0) {
    return null;
  }
  
  // If no quickOptions from API, calculate from hotels array
  const options = quickOptions || calculateQuickOptions(hotels);
  
  if (!options || (!options.cheapest && !options.bestRated && !options.bestLocation)) {
    return null;
  }

  const handleBookClick = (hotel, type, e) => {
    e.stopPropagation();
    // Find the hotel and open the correct booking link based on type
    const fullHotel = hotels?.find(h => h.id === hotel?.id);
    
    if (fullHotel?.bookingLinks) {
      // Map option type to correct booking link
      // bookingLinks order: [0]=Google Hotels, [1]=Booking.com, [2]=Expedia
      let linkIndex = 0;
      if (type === 'cheapest') linkIndex = 1;      // Booking.com
      else if (type === 'bestRated') linkIndex = 0; // Google Hotels
      else if (type === 'bestLocation') linkIndex = 2; // Expedia
      
      const link = fullHotel.bookingLinks[linkIndex] || fullHotel.bookingLinks[0];
      if (link?.url) {
        window.open(link.url, '_blank', 'noopener,noreferrer');
      }
    }
    
    if (onSelectHotel) {
      onSelectHotel(hotel);
    }
  };

  return (
    <div className="hotel-quick-options">
      {/* Cheapest */}
      {options.cheapest && (
        <div className="quick-option-card">
          <div className="option-label">CHEAPEST</div>
          <div className="option-price">
            <strong>${options.cheapest.price}</strong>/night at Booking.com
          </div>
          <button 
            className="option-book-btn"
            onClick={(e) => handleBookClick(options.cheapest, 'cheapest', e)}
          >
            Book Now →
          </button>
        </div>
      )}

      {/* Best Rated */}
      {options.bestRated && (
        <div className="quick-option-card">
          <div className="option-label">BEST RATED</div>
          <div className="option-price">
            <strong>${options.bestRated.price}</strong>/night at Google Hotels
          </div>
          <button 
            className="option-book-btn"
            onClick={(e) => handleBookClick(options.bestRated, 'bestRated', e)}
          >
            Book Now →
          </button>
        </div>
      )}

      {/* Best Location */}
      {options.bestLocation && (
        <div className="quick-option-card">
          <div className="option-label">BEST LOCATION</div>
          <div className="option-price">
            <strong>${options.bestLocation.price}</strong>/night at Expedia
          </div>
          <button 
            className="option-book-btn"
            onClick={(e) => handleBookClick(options.bestLocation, 'bestLocation', e)}
          >
            Book Now →
          </button>
        </div>
      )}
    </div>
  );
};

// Fallback calculation if API doesn't provide quickOptions
function calculateQuickOptions(hotels) {
  if (!hotels || hotels.length === 0) return null;
  
  const validHotels = hotels.filter(h => h.pricePerNight && h.pricePerNight > 0);
  if (validHotels.length === 0) return null;
  
  const sortedByPrice = [...validHotels].sort((a, b) => a.pricePerNight - b.pricePerNight);
  const sortedByRating = [...validHotels].sort((a, b) => (parseFloat(b.rating) || 0) - (parseFloat(a.rating) || 0));
  const sortedByLocation = [...validHotels].sort((a, b) => 
    (a.location?.distanceToDowntown || 999) - (b.location?.distanceToDowntown || 999)
  );
  
  return {
    cheapest: sortedByPrice[0] ? {
      name: sortedByPrice[0].name,
      price: sortedByPrice[0].pricePerNight,
      id: sortedByPrice[0].id
    } : null,
    bestRated: sortedByRating[0] ? {
      name: sortedByRating[0].name,
      price: sortedByRating[0].pricePerNight,
      rating: sortedByRating[0].rating,
      id: sortedByRating[0].id
    } : null,
    bestLocation: sortedByLocation[0] ? {
      name: sortedByLocation[0].name,
      price: sortedByLocation[0].pricePerNight,
      distance: sortedByLocation[0].location?.distanceToDowntown,
      id: sortedByLocation[0].id
    } : null
  };
}

export default HotelQuickOptions;
